import { cx } from '../lib/cx';
import { itemState, type CalendarDay, type CalendarItemState } from '../lib/calendarGrid';

export interface CalendarMonthProps {
  days: readonly CalendarDay[];
  weekdayLabels: readonly string[];
  selectedDay: string | null;
  onSelectDay: (day: string) => void;
  formatDayNumber: (day: string) => string;
  dayAriaLabel: (day: CalendarDay) => string;
  className?: string;
}

/**
 * Stanje nosi i oblik, ne samo boja: odrađeno je puna točka, propušteno prsten,
 * predstojeće blijeda točka, termin kvadratić, a termin koji je klijent sam
 * zakazao prazan kvadratić s rubom. Tako se čita i u sivim tonovima.
 */
const DOT: Record<CalendarItemState, string> = {
  done: 'bg-success',
  missed: 'border-2 border-danger bg-transparent',
  upcoming: 'bg-accent',
  event: 'rounded-[2px] bg-info',
  planned: 'rounded-[2px] border-2 border-accent bg-transparent',
};

export function CalendarMonth({
  days,
  weekdayLabels,
  selectedDay,
  onSelectDay,
  formatDayNumber,
  dayAriaLabel,
  className,
}: CalendarMonthProps) {
  return (
    <div className={cx('w-full', className)}>
      <div className="mb-2 grid grid-cols-7 gap-1">
        {weekdayLabels.map((label) => (
          <span key={label} className="text-center text-[11px] tracking-[0.3px] text-ink-muted">
            {label}
          </span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
        {days.map((day) => {
          const selected = day.day === selectedDay;
          return (
            <button
              key={day.day}
              type="button"
              aria-label={dayAriaLabel(day)}
              aria-pressed={selected}
              aria-current={day.isToday ? 'date' : undefined}
              onClick={() => onSelectDay(day.day)}
              data-day={day.day}
              data-today={day.isToday || undefined}
              className={cx(
                'flex min-h-[56px] flex-col items-center gap-1 rounded-md border p-1.5 transition-colors duration-200',
                day.inMonth ? 'bg-surface' : 'bg-bg-alt/50',
                selected
                  ? 'border-2 border-accent'
                  : day.isToday
                    ? 'border border-accent-dark'
                    : 'border-line-subtle hover:border-line-strong',
              )}
            >
              <span
                className={cx(
                  'text-[12px] tabular-nums',
                  day.inMonth ? 'text-ink' : 'text-ink-muted',
                  day.isToday && 'font-semibold text-accent-dark',
                )}
              >
                {formatDayNumber(day.day)}
              </span>
              <span className="flex flex-wrap justify-center gap-0.5">
                {day.items.slice(0, 4).map((item) => (
                  <span
                    key={`${item.kind}-${item.id}`}
                    aria-hidden="true"
                    className={cx('block h-1.5 w-1.5 rounded-full', DOT[itemState(item)])}
                  />
                ))}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
